import { useEffect, useState } from "react";
import classNames from "classnames";
import { Formik, setNestedObjectValues, useFormikContext } from "formik";
import { get, has, isEmpty } from "lodash";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { BiCheck, BiX } from "react-icons/bi";
import { NAME_SPACES } from "shared/locales/constants";
import ApiAccess from "../Detail/ApiAccess";
import EncryptionKey from "../Detail/EncryptionKey";
import Environment from "../Detail/Environment";
import SetupList from "../Detail/SetupList";
import {
  ENCRYPTION_STATUSES,
  environmentTodoOf,
  keyStatusOf,
  laterFieldLabelsOf,
  SETUP_SECTIONS,
  setupItemsOf,
  setupStatusOf,
  setupTodosOf,
  STATUS_TONES,
  todoStatusOf,
} from "../Detail/setup";
import GeneralStep from "./GeneralStep";
import IntegrationsStep from "./IntegrationsStep";
import {
  isExternal,
  laterFields,
  STEP_KEYS,
  stepIndexOf,
  stepsOf,
} from "./onboarding";
import RemainingPanel from "./RemainingPanel";
import validation from "./validation";

const Wizard = ({
  created,
  catalog,
  setup,
  keys,
  api,
  environment,
  saving,
  onClose,
  onFinish,
  onSaveIntegrations,
  onSaveEnvironment,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.ORGANIZATION);
  const LABELS = t("ONBOARDING", { returnObjects: true });
  const DETAIL = t("DETAIL", { returnObjects: true });
  const ENCRYPTION = DETAIL.ENCRYPTION;
  const { t: brokerT } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.BROKER);
  const formOptions = brokerT("COMPANY_FORM_OPTIONS", { returnObjects: true });
  const { values, validateForm, setTouched, submitForm } = useFormikContext();
  const hosting = get(values, "organization.hosting");
  const steps = stepsOf(hosting);
  const stepIndex = stepIndexOf(hosting);
  const opening =
    stepIndex[isExternal(hosting) ? STEP_KEYS.ENVIRONMENT : STEP_KEYS.KEY];
  const [step, setStep] = useState(stepIndex.GENERAL);
  const stepKey = steps[step];
  const type = get(values, "organization.type");

  useEffect(() => {
    if (created) setStep(opening);
  }, [created, opening]);

  const later = laterFields(values);
  const keyStatus = keyStatusOf(keys.encryption);
  const keysLocked = keyStatus !== ENCRYPTION_STATUSES.ACTIVE;

  const setupContext = {
    keyStatus,
    access: api.access,
    labels: DETAIL,
    t,
  };

  const todo = created
    ? {
        ...setupTodosOf({ ...setupContext, values, catalog, setup, type }),
        [STEP_KEYS.ENVIRONMENT]: environmentTodoOf(
          environment.ready,
          DETAIL.ENVIRONMENT,
        ),
      }
    : {
        [STEP_KEYS.GENERAL]: laterFieldLabelsOf(later, LABELS.GENERAL),
        [STEP_KEYS.KEY]: [LABELS.REMAINING.KEY],
        [STEP_KEYS.ENVIRONMENT]: [DETAIL.ENVIRONMENT.TODO],
        [STEP_KEYS.PLANS]: get(LABELS.LATER.PLANS, type) || [],
        [STEP_KEYS.DOCUMENTS]: LABELS.LATER.DOCUMENTS || [],
      };

  const statusOf = (key) => {
    if (!created && key !== STEP_KEYS.GENERAL)
      return { tone: STATUS_TONES.AFTER, text: LABELS.STATUS.AFTER };
    if (!created && later.length > 0)
      return {
        tone: STATUS_TONES.TODO,
        text: t("ONBOARDING.STATUS.LATER", { count: later.length }),
      };
    if (key === STEP_KEYS.GENERAL)
      return {
        tone: STATUS_TONES.DONE,
        text: created ? LABELS.STATUS.CREATED : LABELS.STATUS.COMPLETE,
      };
    const setupStatus = setupStatusOf(key, setupContext);
    if (setupStatus) return setupStatus;
    if (!isEmpty(todo[key])) return todoStatusOf(key, todo[key], t);
    return { tone: STATUS_TONES.DONE, text: LABELS.STATUS.COMPLETE };
  };

  const reachable = (index) =>
    created ? index !== stepIndex.GENERAL : index === stepIndex.GENERAL;

  const SAVES = {
    [STEP_KEYS.INTEGRATIONS]: onSaveIntegrations,
    [STEP_KEYS.ENVIRONMENT]: onSaveEnvironment,
  };

  const leave = () =>
    has(SAVES, stepKey) ? SAVES[stepKey](values) : Promise.resolve();

  const goTo = (index) => {
    if (!reachable(index) || index === step) return null;
    return leave()
      .then(() => setStep(index))
      .catch(() => null);
  };

  const finish = () =>
    leave()
      .then(onFinish)
      .catch(() => null);

  const close = () =>
    leave()
      .then(onClose)
      .catch(() => null);

  const create = async () => {
    const errors = await validateForm();
    if (!isEmpty(errors)) {
      setTouched(setNestedObjectValues(errors, true));
      return;
    }
    submitForm();
  };

  const groups = steps
    .filter((key) => !isEmpty(todo[key]))
    .map((key) => ({
      step: key,
      index: stepIndex[key],
      title: get(LABELS.STEPS, [key, "TITLE"]),
      items: todo[key],
    }));

  const last = step === steps.length - 1;

  return (
    <div className="onboarding">
      <header className="onboarding__header">
        <div className="onboarding__header-inner">
          <div className="onboarding__heading">
            <span className="onboarding__eyebrow">{LABELS.EYEBROW}</span>
            <h1 className="onboarding__title">{LABELS.TITLE}</h1>
          </div>
          <button
            type="button"
            className="onboarding__close"
            aria-label={LABELS.CLOSE}
            onClick={close}
          >
            <BiX size={20} aria-hidden="true" />
          </button>
        </div>
      </header>

      <div className="onboarding__body">
        <nav aria-label={LABELS.STEPS_LABEL} className="onboarding__nav">
          <ol className="onboarding__steps onboarding__steps--numbered">
            {steps.map((key, index) => {
              const current = index === step;
              const status = statusOf(key);
              const done = status.tone === STATUS_TONES.DONE && !current;
              return (
                <li key={key}>
                  <button
                    type="button"
                    aria-current={current ? "step" : undefined}
                    disabled={!reachable(index)}
                    className={classNames("onboarding__step", {
                      "onboarding__step--current": current,
                      "onboarding__step--done": done,
                    })}
                    onClick={() => goTo(index)}
                  >
                    <span className="onboarding__step-mark">
                      {done ? (
                        <BiCheck size={16} aria-hidden="true" />
                      ) : (
                        index + 1
                      )}
                    </span>
                    <span className="onboarding__step-text">
                      <span className="onboarding__step-title">
                        {get(LABELS.STEPS, [key, "TITLE"])}
                      </span>
                      <span
                        className={`onboarding__step-status onboarding__step-status--${status.tone}`}
                      >
                        {status.text}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        <section
          aria-labelledby="onboarding-step-title"
          className="onboarding__main"
        >
          <div className="onboarding__main-head">
            <span className="onboarding__eyebrow">
              {t("ONBOARDING.STEP_OF", {
                current: step + 1,
                total: steps.length,
              })}
            </span>
            <h2 id="onboarding-step-title" className="onboarding__main-title">
              {get(LABELS.STEPS, [stepKey, "TITLE"])}
            </h2>
            <p className="onboarding__lead">
              {get(LABELS.STEPS, [stepKey, "LEAD"])}
            </p>
          </div>
          <div className="onboarding__content">
            {stepKey === STEP_KEYS.GENERAL ? (
              <GeneralStep
                labels={LABELS.GENERAL}
                formOptions={formOptions}
                hosting={hosting}
              />
            ) : null}
            {stepKey === STEP_KEYS.KEY && !keys.loading ? (
              <EncryptionKey
                encryption={keys.encryption}
                labels={{ ...ENCRYPTION, HANDOVER: DETAIL.HANDOVER }}
                busy={keys.busy}
                adminEmail={get(values, "user.email")}
                onLink={keys.onLink}
                onPrepare={keys.onPrepare}
                onImport={keys.onImport}
                onVerify={keys.onVerify}
                onSendHandover={keys.onSendHandover}
              />
            ) : null}
            {stepKey === STEP_KEYS.INTEGRATIONS ? (
              <IntegrationsStep
                catalog={catalog}
                labels={LABELS.INTEGRATIONS}
                keysLocked={keysLocked}
                lockedLabels={ENCRYPTION}
                onConfigureKey={() => goTo(stepIndex.KEY)}
              />
            ) : null}
            {stepKey === STEP_KEYS.API && !api.loading ? (
              <ApiAccess
                access={api.access}
                labels={{ ...DETAIL.API, HANDOVER: DETAIL.HANDOVER }}
                busy={api.busy}
                adminEmail={get(values, "user.email")}
                onToggle={api.onToggle}
                onCreate={api.onCreate}
                onDelete={api.onDelete}
                onResend={api.onResend}
              />
            ) : null}
            {stepKey === STEP_KEYS.ENVIRONMENT ? (
              <Environment
                ready={environment.ready}
                labels={DETAIL.ENVIRONMENT}
              />
            ) : null}
            {SETUP_SECTIONS.includes(stepKey) ? (
              <SetupList
                items={setupItemsOf(stepKey, type)}
                setup={setup}
                labels={DETAIL.SETUP}
                withIcon={stepKey === STEP_KEYS.DOCUMENTS}
              />
            ) : null}
          </div>
          <div className="onboarding__footer">
            {created && step > opening ? (
              <button
                type="button"
                className="onboarding__button onboarding__button--outline"
                disabled={saving}
                onClick={() => goTo(step - 1)}
              >
                {LABELS.PREVIOUS}
              </button>
            ) : null}
            {created ? (
              <button
                type="button"
                className="onboarding__button onboarding__button--primary onboarding__push"
                disabled={saving}
                onClick={() => (last ? finish() : goTo(step + 1))}
              >
                {last ? LABELS.FINISH : LABELS.NEXT}
              </button>
            ) : (
              <button
                type="button"
                className="onboarding__button onboarding__button--primary onboarding__push"
                disabled={saving}
                onClick={create}
              >
                {saving ? LABELS.CREATING : LABELS.CREATE}
              </button>
            )}
          </div>
        </section>

        <RemainingPanel
          groups={groups}
          labels={
            created
              ? {
                  ...LABELS.REMAINING,
                  COUNT: DETAIL.REMAINING.COUNT,
                  NOTE: null,
                }
              : {
                  ...LABELS.REMAINING,
                  CREATE: LABELS.CREATE,
                  CREATING: LABELS.CREATING,
                }
          }
          saving={saving}
          onGo={goTo}
          onCreate={created ? null : create}
        />
      </div>
    </div>
  );
};

Wizard.propTypes = {
  created: PropTypes.bool.isRequired,
  catalog: PropTypes.arrayOf(PropTypes.object).isRequired,
  setup: PropTypes.object.isRequired,
  keys: PropTypes.object.isRequired,
  api: PropTypes.object.isRequired,
  environment: PropTypes.object.isRequired,
  saving: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onFinish: PropTypes.func.isRequired,
  onSaveIntegrations: PropTypes.func.isRequired,
  onSaveEnvironment: PropTypes.func.isRequired,
};

const View = ({ initialValues, onSubmit, ...props }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.ORGANIZATION);
  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      )}
      onSubmit={onSubmit}
    >
      <Wizard {...props} />
    </Formik>
  );
};

View.propTypes = {
  initialValues: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default View;
